import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Thermometer, Calculator, Home } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './HeatLossCalculatorPage/components/CalculationForm';
import ResultsOverview from './HeatLossCalculatorPage/components/ResultsOverview';
import HeatBalanceVisualization from './HeatLossCalculatorPage/components/HeatBalanceVisualization';
import QuickAdjustmentSidebar from './HeatLossCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './HeatLossCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './HeatLossCalculatorPage/components/TipsSidebar';
import InfoContent from './HeatLossCalculatorPage/components/InfoContent';

// Климатические данные для регионов России
import { climateData } from './HeatLossCalculatorPage/data/climateData';
import { buildingMaterials } from './HeatLossCalculatorPage/data/buildingMaterials';
import { windowTypes } from './HeatLossCalculatorPage/data/windowTypes';

// Определение начального состояния формы
export const initialFormData = {
  // Общие параметры здания
  buildingType: 'residential',
  region: 'moscow',
  cityName: '',
  outdoorTemperature: -25,
  indoorTemperature: 20,
  buildingAge: 'new',
  
  // Размеры здания
  floorArea: '100',
  ceilingHeight: '2.7',
  floors: '1',
  
  // Материалы и конструкции
  wallMaterial: 'brick_insulated',
  wallThickness: '510',
  insulationMaterial: 'mineral_wool',
  insulationThickness: '100',
  windowType: 'double_glazed_modern',
  windowArea: '15',
  roofType: 'insulated',
  roofMaterial: 'concrete_insulated',
  roofInsulationThickness: '150',
  floorType: 'insulated',
  floorMaterial: 'concrete_insulated',
  floorInsulationThickness: '100',
  
  // Системы вентиляции и инфильтрации
  ventilationType: 'natural',
  hasHeatRecovery: false,
  recoveryEfficiency: '70',
  infiltrationRate: 'medium',
  
  // Дополнительные параметры
  includeTemperatureFactor: true,
  includeThermalBridges: true,
  includeInfiltrationLoss: true
};

/**
 * HeatLossCalculatorPage Component - Калькулятор теплопотерь здания с учетом различных строительных конструкций и климатических условий
 */
const HeatLossCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('heat-loss-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор теплопотерь дома | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет теплопотерь дома и квартиры онлайн: с учетом характеристик здания, климатической зоны, материалов стен, окон, крыши и пола. Подбор оптимальной системы отопления для заданных условий.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [heatBalanceData, setHeatBalanceData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showFullResults, setShowFullResults] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedHeatLossCalculations');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'residential':
          newState.buildingType = 'residential';
          break;
        case 'apartment':
          newState.buildingType = 'apartment';
          break;
        case 'commercial':
          newState.buildingType = 'commercial';
          break;
        case 'with-ventilation':
          newState.includeInfiltrationLoss = true;
          newState.ventilationType = 'mechanical';
          break;
        case 'old-building':
          newState.buildingAge = 'old';
          newState.wallMaterial = 'brick_noninsulated';
          newState.windowType = 'single_glazed';
          break;
        case 'new-building':
          newState.buildingAge = 'new';
          newState.wallMaterial = 'brick_insulated';
          newState.windowType = 'double_glazed_modern';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setHeatBalanceData(null);
    setErrors({});
    setShowFullResults(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Дом ${formData.floorArea}м² в ${climateData[formData.region]?.name || formData.region}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      buildingType: formData.buildingType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedHeatLossCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedHeatLossCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      // Также можно восстановить данные визуализации теплового баланса
      if (calculationToLoad.results && calculationToLoad.results.heatLosses) {
        setHeatBalanceData({
          labels: [
            'Стены', 
            'Окна', 
            'Крыша/потолок', 
            'Пол', 
            'Вентиляция/инфильтрация'
          ],
          data: [
            calculationToLoad.results.heatLosses.walls,
            calculationToLoad.results.heatLosses.windows,
            calculationToLoad.results.heatLosses.roof,
            calculationToLoad.results.heatLosses.floor,
            calculationToLoad.results.heatLosses.ventilation
          ]
        });
      }
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Thermometer className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор теплопотерь дома'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setHeatBalanceData={setHeatBalanceData}
              saveCalculation={saveCalculation}
              climateData={climateData}
              buildingMaterials={buildingMaterials}
              windowTypes={windowTypes}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <HeatBalanceVisualization 
                  data={heatBalanceData} 
                  results={results}
                  showFullResults={showFullResults}
                  setShowFullResults={setShowFullResults}
                />
              </>
            )}

            {/* --- Информационный контент о теплопотерях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              climateData={climateData}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .heat-chart-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .heat-chart-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .heat-chart-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .heat-chart-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .heat-chart-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .heat-chart-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .heat-chart-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .heat-chart-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .heat-chart-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default HeatLossCalculatorPage;